/**
 * Role Management — edit role_definitions.ui_access in scenario_os config_store.
 * Platform admin only (enforced by API + nav).
 */
import React, { useCallback, useEffect, useState } from 'react';
import CoachLayout from './CoachLayout';
import { useAuth } from '../../context/AuthContext';

function authHeaders() {
  const token = localStorage.getItem('scenario_os_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const UI_KEYS = [
  'domain_builder', 'content_studio', 'role_management', 'org_management',
  'user_management', 'billing', 'analytics', 'learner_app',
];

const ACCESS_OPTIONS = [
  { value: 'true', label: 'Full' },
  { value: 'false', label: 'Hidden' },
  { value: 'view_only', label: 'View only' },
  { value: 'read_only', label: 'Read only' },
  { value: 'restricted', label: 'Restricted' },
];

function serializeAccess(v) {
  if (v === true) return 'true';
  if (v === false || v == null) return 'false';
  return String(v);
}

function parseAccess(v) {
  if (v === 'true') return true;
  if (v === 'false') return false;
  return v;
}

export default function AdminRolesPage() {
  const { isPlatformAdmin } = useAuth();
  const [roles, setRoles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [uiAccess, setUiAccess] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    fetch('/scenario-os/admin/roles', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : r.json().then((d) => Promise.reject(d))))
      .then((data) => setRoles(data.roles || []))
      .catch((e) => setError(e.detail || e.message || 'Failed to load roles'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openRole = (role) => {
    setSelected(role);
    setUiAccess({ ...(role.ui_access || {}) });
    setError('');
    setSuccess('');
  };

  const save = async () => {
    if (!selected) return;
    setError('');
    setSuccess('');
    try {
      const r = await fetch(`/scenario-os/admin/roles/${encodeURIComponent(selected.role_id)}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ ui_access: uiAccess }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Save failed');
      setSuccess('Saved. Coaches pick up nav changes on next login/refresh.');
      setSelected(data.role);
      load();
    } catch (e) {
      setError(e.message || 'Save failed');
    }
  };

  if (!isPlatformAdmin) {
    return (
      <CoachLayout title="Role Management">
        <div style={{ padding: 24, color: '#991b1b' }}>Platform admin required.</div>
      </CoachLayout>
    );
  }

  return (
    <CoachLayout title="Role Management">
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20, padding: 8 }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 12 }}>Roles</div>
          {loading && <div style={{ color: '#6b7280', fontSize: 13 }}>Loading…</div>}
          {roles.map((role) => (
            <button
              key={role.role_id}
              type="button"
              onClick={() => openRole(role)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px',
                marginBottom: 6, borderRadius: 8, border: '1px solid #e5e7eb',
                background: selected?.role_id === role.role_id ? '#eef2ff' : '#fff',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 14 }}>{role.display_name || role.role_id}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{role.role_id} · {role.scope}</div>
            </button>
          ))}
        </div>

        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
          {!selected ? (
            <div style={{ color: '#6b7280' }}>Select a role to edit UI access.</div>
          ) : (
            <>
              <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>{selected.display_name}</h2>
              <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: 13 }}>{selected.description}</p>
              {error && <div style={{ color: '#991b1b', marginBottom: 12 }}>{error}</div>}
              {success && <div style={{ color: '#166534', marginBottom: 12 }}>{success}</div>}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ padding: '8px 6px' }}>UI surface</th>
                    <th style={{ padding: '8px 6px' }}>Access</th>
                  </tr>
                </thead>
                <tbody>
                  {UI_KEYS.map((key) => (
                    <tr key={key} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 6px', fontFamily: 'monospace' }}>{key}</td>
                      <td style={{ padding: '10px 6px' }}>
                        <select
                          value={serializeAccess(uiAccess[key])}
                          onChange={(e) => setUiAccess((prev) => ({
                            ...prev,
                            [key]: parseAccess(e.target.value),
                          }))}
                          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
                        >
                          {ACCESS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                onClick={save}
                style={{
                  marginTop: 16, padding: '10px 18px', borderRadius: 8, border: 'none',
                  background: '#4f46e5', color: '#fff', fontWeight: 600, cursor: 'pointer',
                }}
              >
                Save ui_access
              </button>
            </>
          )}
        </div>
      </div>
    </CoachLayout>
  );
}
